import { useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { OrgInfoInterface } from '../../../../../../types/ClientApplication';
import { UploadedFiles } from '../../../../../../types/Shared';
import { formatDate3 } from '../../../../../../utils';
import { EinFormatDisplay } from '../../../../../UI/NumberFormatDisplay/EinFormatDisplay';
import { PhoneNumberFormatDisplay } from '../../../../../UI/NumberFormatDisplay/PhoneNumberFormatDisplay';
import { SocialSecurityFormatDisplay } from '../../../../../UI/NumberFormatDisplay/SocialSecurityFormatDisplay';
import { ApplicationAttachments } from '../../../ApplicationAttachments';

export default function CCLFApplicationInformation({
  organizationInformation,
  files,
}: {
  organizationInformation: OrgInfoInterface;
  files: UploadedFiles[];
}) {
  const { id = 0 } = useParams();
  const queryClient = useQueryClient();
  const getFiles = (sponsor: any) =>
    files?.reduce<{
      [id: string]: UploadedFiles[];
    }>(
      (acc, current) => {
        if (current.field.includes(`corporateTaxReturns:${sponsor.arrayId}`)) {
          acc = {
            ...acc,
            corporateTaxReturns: [...acc.corporateTaxReturns, current],
          };
        }
        if (current.field.includes(`personalTaxReturns:${sponsor.arrayId}`)) {
          acc = {
            ...acc,
            personalTaxReturns: [...acc.personalTaxReturns, current],
          };
        }
        return acc;
      },
      {
        corporateTaxReturns: [],
        personalTaxReturns: [],
      }
    );

  const businessTaxReturns = files?.filter(
    (file) => file.field === 'orgInfo.businessTaxReturns'
  );
  return (
    <>
      {organizationInformation && (
        <section aria-labelledby='applicant-information-title'>
          <div className='bg-white shadow sm:rounded-lg'>
            <div className='py-5'>
              <div className='px-4 py-2 sm:px-6 border-l-4 border-primaryColor-normal'>
                <h2
                  id='applicant-information-title'
                  className='m-0 text-lg leading-6 font-medium text-primaryColor-normal'
                >
                  Application Information
                </h2>
                <p className='mt-1 max-w-2xl text-sm text-gray-500'>
                  Details of an application.
                </p>
              </div>
            </div>
            <div className='border-t border-gray-200 px-4 py-5 sm:px-6 space-y-5'>
              <dl className='grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2'>
                <div className='sm:col-span-1'>
                  <dt className='text-sm font-medium text-gray-500'>
                    Business Name
                  </dt>
                  <dd className='mt-1 text-sm text-gray-900'>
                    {organizationInformation.businessName}
                  </dd>
                </div>
                <div className='sm:col-span-1'>
                  <dt className='text-sm font-medium text-gray-500'>
                    Business Address
                  </dt>
                  <dd className='mt-1 text-sm text-gray-900'>
                    {organizationInformation.businessAddress},{' '}
                    {organizationInformation.businessCity},{' '}
                    {organizationInformation.state},{' '}
                    {organizationInformation.zipCode}
                  </dd>
                </div>
                <div className='sm:col-span-1'>
                  <dt className='text-sm font-medium text-gray-500'>
                    Date Established
                  </dt>
                  <dd className='mt-1 text-sm text-gray-900'>
                    {formatDate3(organizationInformation.establishedDate)}
                  </dd>
                </div>
                <div className='sm:col-span-1'>
                  <dt className='text-sm font-medium text-gray-500'>
                    Legal Status
                  </dt>
                  <dd className='mt-1 text-sm text-gray-900 uppercase'>
                    {organizationInformation.legalStatus}
                  </dd>
                </div>
                <div className='sm:col-span-1'>
                  <dt className='text-sm font-medium text-gray-500'>
                    Business Phone
                  </dt>
                  <dd className='mt-1 text-sm text-gray-900 capitalize'>
                    <PhoneNumberFormatDisplay
                      value={organizationInformation.businessPhone}
                    />
                  </dd>
                </div>
                <div className='sm:col-span-1'>
                  <dt className='text-sm font-medium text-gray-500'>
                    Lead Contact
                  </dt>
                  <dd className='mt-1 text-sm text-gray-900'>
                    {organizationInformation.leadContact}
                  </dd>
                </div>
                <div className='sm:col-span-1'>
                  <dt className='text-sm font-medium text-gray-500'>
                    Contact Information
                  </dt>
                  <dd className='mt-1 text-sm text-gray-900 flex flex-col'>
                    <span>{organizationInformation.email}</span>
                    <span>
                      <PhoneNumberFormatDisplay
                        value={organizationInformation.phone}
                      />
                    </span>
                  </dd>
                </div>
                <div className='sm:col-span-1'>
                  <dt className='text-sm font-medium text-gray-500'>FEIN</dt>
                  <dd className='mt-1 text-sm text-gray-900'>
                    {organizationInformation.fein ? (
                      <EinFormatDisplay value={organizationInformation.fein} />
                    ) : (
                      'None'
                    )}
                  </dd>
                </div>
                <div className='sm:col-span-1'>
                  <dt className='text-sm font-medium text-gray-500'>Website</dt>
                  <dd className='mt-1 text-sm text-gray-900'>
                    {organizationInformation.website ? (
                      <a
                        href={`https://${organizationInformation.website}`}
                        className='underline text-blue-500'
                        target='_blank'
                        rel='noreferrer'
                      >
                        {organizationInformation.website}
                      </a>
                    ) : (
                      <span>None</span>
                    )}
                  </dd>
                </div>
                <div className='sm:col-span-1'>
                  <dt className='text-sm font-medium text-gray-500'>
                    CCLF Financing
                  </dt>
                  <dd className='mt-1 text-sm text-gray-900'>
                    {organizationInformation.cclfFinancing ? 'Yes' : 'No'}
                  </dd>
                </div>
                <div className='sm:col-span-1'>
                  <dt className='text-sm font-medium text-gray-500'>
                    Social Designation
                  </dt>
                  <dd className='mt-1 text-sm text-gray-900 capitalize'>
                    {organizationInformation.socialDesignation}
                  </dd>
                </div>
                <div className='col-span-2'>
                  <ApplicationAttachments
                    title='Please upload the past three years of tax returns and the most recent financial statements.'
                    files={businessTaxReturns}
                    field={`orgInfo.businessTaxReturns`}
                    applicationId={id as number}
                    onUploadOrDeleteActionSuccess={() => {
                      queryClient.invalidateQueries(['get-client-application']);
                    }}
                  />
                </div>
              </dl>
              <div className='space-y-2'>
                <h3>Sponsors</h3>
                <dl className='space-y-5'>
                  {organizationInformation.sponsors.map(
                    (sponsor: any, index) => {
                      const { corporateTaxReturns, personalTaxReturns } =
                        getFiles(sponsor);
                      return (
                        <div
                          key={sponsor.socialSecurity}
                          className='grid grid-cols-2 gap-5 border p-4 rounded-md'
                        >
                          <div>
                            <dt className='text-neutralColor-normal'>
                              Information
                            </dt>
                            <dd className='flex flex-col'>
                              <span>
                                {sponsor.name} ({sponsor.ownershipPercent}%)
                              </span>
                              <span>{sponsor.address}</span>
                              <span>
                                {sponsor.city}, {sponsor.state},{' '}
                                {sponsor.zipCode}
                              </span>
                              <span>
                                SS#:{' '}
                                <SocialSecurityFormatDisplay
                                  value={sponsor.socialSecurity}
                                />{' '}
                              </span>
                            </dd>
                          </div>
                          <div>
                            <dt className='text-neutralColor-normal'>
                              Contact Information
                            </dt>
                            <dd className='flex flex-col'>
                              <span className='truncate'>
                                {sponsor.leadContact}
                              </span>
                              <span className='truncate'>{sponsor.email}</span>
                              <span>
                                <PhoneNumberFormatDisplay
                                  value={sponsor.phone}
                                />
                              </span>
                            </dd>
                          </div>
                          {!sponsor.isCorporate && (
                            <div className='col-span-2'>
                              <ApplicationAttachments
                                title='Please upload the past three years of tax returns and your personal financial statement.'
                                files={personalTaxReturns}
                                field={`orgInfo.sponsors.${index}.personalTaxReturns:${sponsor.arrayId}`}
                                applicationId={id as number}
                                onUploadOrDeleteActionSuccess={() => {
                                  queryClient.invalidateQueries([
                                    'get-client-application',
                                  ]);
                                }}
                              />
                            </div>
                          )}
                          <div className='col-span-2'>
                            {sponsor.isCorporate && (
                              <p className='mb-2'>Corporate</p>
                            )}
                            <div className='flex flex-col'>
                              {sponsor.isCorporate && (
                                <div className='grid grid-cols-2 gap-4'>
                                  <div>
                                    <dt className='text-neutralColor-normal'>
                                      Established Date
                                    </dt>
                                    <dd>
                                      {formatDate3(sponsor.establishedDate)}
                                    </dd>
                                  </div>
                                  <div>
                                    <dt className='text-neutralColor-normal'>
                                      FEIN
                                    </dt>
                                    <dd>{sponsor.fein}</dd>
                                  </div>
                                  <div>
                                    <dt className='text-neutralColor-normal'>
                                      Website
                                    </dt>
                                    <dd>{sponsor.website}</dd>
                                  </div>
                                  <div>
                                    <dt className='text-neutralColor-normal'>
                                      Lead Contact
                                    </dt>
                                    <dd className='flex flex-col'>
                                      <span>{sponsor.leadContact}</span>
                                      <span>{sponsor.corporateEmail}</span>
                                      <PhoneNumberFormatDisplay
                                        value={sponsor.corporatePhone}
                                      />
                                    </dd>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>

                          {sponsor.isCorporate && (
                            <div className='col-span-2'>
                              <ApplicationAttachments
                                title='Corporate Tax Returns'
                                files={corporateTaxReturns}
                                field={`orgInfo.sponsors.${index}.corporateTaxReturns:${sponsor.arrayId}`}
                                applicationId={id as number}
                                onUploadOrDeleteActionSuccess={() => {
                                  queryClient.invalidateQueries([
                                    'get-client-application',
                                  ]);
                                }}
                              />
                            </div>
                          )}
                        </div>
                      );
                    }
                  )}
                </dl>
              </div>
            </div>
          </div>
        </section>
      )}
    </>
  );
}
